import { useState, useEffect } from 'react';
import { Copy, Check, Key } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { getWebhookAuthToken } from '@/lib/firebase/functions';
import { useAppStore } from '@/stores/app-store';
import { useModuleStore } from '@/stores/module-store';

/**
 * Shows the Ulgebra auth token in a dialog when ?ua_target_action=show_tpa_uatoken
 * is present in the URL. Used by external integrations that need the token.
 *
 * Mirrors the old app's ulgebraAuthTokenShowFunction.
 */
export function AuthTokenDialog() {
  const extensionName = useAppStore((s) => s.extensionName);
  const tpaAuthenticated = useModuleStore((s) => s.tpaAuthenticated);
  const [open, setOpen] = useState(false);
  const [token, setToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  // Check URL param on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('ua_target_action') === 'show_tpa_uatoken') {
      setOpen(true);
    }
  }, []);

  // Fetch token when dialog opens and TPA is authenticated
  useEffect(() => {
    if (!open || !extensionName || !tpaAuthenticated) return;
    setLoading(true);
    getWebhookAuthToken(extensionName)
      .then((result) => {
        if (result?.saas) setToken(result.saas);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [open, extensionName, tpaAuthenticated]);

  const handleCopy = async () => {
    if (!token) return;
    try {
      await navigator.clipboard.writeText(token);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = token;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent>
        <DialogClose onClose={() => setOpen(false)} />
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Key className="h-4 w-4" />
            Auth Token
          </DialogTitle>
          <DialogDescription>
            Your Ulgebra webhook authentication token
          </DialogDescription>
        </DialogHeader>
        <div className="mt-3 space-y-3">
          {loading ? (
            <p className="text-sm text-muted-foreground">Loading token...</p>
          ) : token ? (
            <>
              <div
                onClick={handleCopy}
                className="text-xs font-mono bg-muted/50 border border-border rounded-md p-3 break-all cursor-pointer select-all text-green-600 dark:text-green-400"
              >
                {token}
              </div>
              <Button size="sm" variant="outline" className="w-full" onClick={handleCopy}>
                {copied ? (
                  <><Check className="h-3.5 w-3.5 mr-1.5" /> Copied</>
                ) : (
                  <><Copy className="h-3.5 w-3.5 mr-1.5" /> Copy Token</>
                )}
              </Button>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">
              {tpaAuthenticated ? 'Failed to load token.' : 'Waiting for authentication...'}
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
